import React, { Fragment, useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import {
  getContentGroupsViewPath,
  getModuleViewPath,
  getReviewViewPath,
  getUngroupedItemsViewPath,
  OrganizeViews,
} from "../organizeUtil";
import { useCurrentOrgId, useIsIapReviewer } from "../../../auth/authUtils";
import SnackAlert from "../../../components/SnackAlert";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { OrganizeContext } from "../organize-context";
import { useSelected } from "../OrganizeProvider";
import {
  useContentByStatus,
  useContentInProcessing,
} from "../../creatorServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleRight } from "@fortawesome/pro-regular-svg-icons";

import TreeView from "@material-ui/lab/TreeView";

import CollapseIcon from "./CollapseIcon";
import SidebarModuleTree from "./SidebarModuleTree";
import SidebarTreeItem from "./SidebarTreeItem";
import SidebarUngroupedContentGroupsTree from "./SidebarUngroupedContentGroupsTree";

const useStyles = makeStyles(theme => ({
  header: {
    fontWeight: theme.typography.fontWeightMedium,
  },

  icon: {
    padding: 2,
  },

  tree: {
    width: "100%",
  },
}));

const OrganizeSidebar = props => {
  const classes = useStyles();
  const history = useHistory();
  const currentOrgId = useCurrentOrgId();
  const isIapReviewer = useIsIapReviewer();
  const { updateDragSourceParent } = useContext(OrganizeContext);

  const { removeAllSelected } = useSelected();

  const snackAlertHandler = useSnackAlert();
  const { showAlert } = snackAlertHandler;

  const { selected, expanded, handleExpanded } = props;

  const handleLabelClick = viewPath => event => {
    event.preventDefault();
    updateDragSourceParent({});
    removeAllSelected();
    history.push(viewPath);
  };

  const handleModuleLabelClick = id => event => {
    removeAllSelected();

    const modulePath = getModuleViewPath(id);
    history.push(modulePath);
  };

  // Content Totals
  const approvedResults = useContentByStatus({ status: "approved" });
  const [approvedCount, setApprovedCount] = useState("-");
  useEffect(() => {
    if (approvedResults.isSuccess) {
      setApprovedCount(approvedResults.data.count);
    }
  }, [approvedResults.isSuccess, approvedResults.data]);

  const pendingResults = useContentByStatus({ status: "pending" });
  const [pendingCount, setPendingCount] = useState("-");
  useEffect(() => {
    if (pendingResults.isSuccess) {
      setPendingCount(pendingResults.data.count);
    }
  }, [pendingResults.isSuccess, pendingResults.data]);

  const rejectedResults = useContentByStatus({ status: "rejected" });
  const [rejectedCount, setRejectedCount] = useState("-");
  useEffect(() => {
    if (rejectedResults.isSuccess) {
      setRejectedCount(rejectedResults.data.count);
    }
  }, [rejectedResults.isSuccess, rejectedResults.data]);

  const processingResults = useContentInProcessing(currentOrgId, {
    limit: 0,
    offset: 0,
  });
  const [processingCount, setProcessingCount] = useState("-");
  useEffect(() => {
    if (processingResults.isSuccess) {
      setProcessingCount(processingResults.data.totalElements);
    }
  }, [processingResults.isSuccess, processingResults.data]);

  const [modulesLoading, setModulesLoading] = useState(false);
  const [ungroupedLoading, setUngroupedLoading] = useState(false);

  return (
    <Fragment>
      <SnackAlert handler={snackAlertHandler} />

      <TreeView
        aria-label="organize content sidebar"
        classes={{ root: classes.tree }}
        defaultCollapseIcon={<FontAwesomeIcon icon={faAngleDown} />}
        defaultExpandIcon={<FontAwesomeIcon icon={faAngleRight} />}
        onNodeToggle={handleExpanded}
        selected={selected}
        expanded={expanded}
      >
        <SidebarTreeItem
          label="Review"
          nodeId={OrganizeViews.REVIEW}
          nodeType="root"
          nestedTree={
            <Fragment>
              {!isIapReviewer && (
                <SidebarTreeItem
                  label={`Processing (${processingCount})`}
                  nodeType="root"
                  // [TODO]: add processing variant
                  variant="pending"
                  nodeId={OrganizeViews.PROCESSING}
                  onLabelClick={handleLabelClick(
                    getReviewViewPath(OrganizeViews.PROCESSING),
                  )}
                />
              )}

              <SidebarTreeItem
                label={`Pending (${pendingCount})`}
                nodeType="root"
                variant="pending"
                nodeId={OrganizeViews.PENDING}
                onLabelClick={handleLabelClick(
                  getReviewViewPath(OrganizeViews.PENDING),
                )}
              />

              <SidebarTreeItem
                label={`Approved (${approvedCount})`}
                nodeType="root"
                variant="approved"
                nodeId={OrganizeViews.APPROVED}
                onLabelClick={handleLabelClick(
                  getReviewViewPath(OrganizeViews.APPROVED),
                )}
              />

              <SidebarTreeItem
                label={`Rejected (${rejectedCount})`}
                nodeType="root"
                variant="rejected"
                nodeId={OrganizeViews.REJECTED}
                onLabelClick={handleLabelClick(
                  getReviewViewPath(OrganizeViews.REJECTED),
                )}
              />
            </Fragment>
          }
        />

        {!isIapReviewer && (
          <SidebarTreeItem
            label="Build"
            nodeId={OrganizeViews.ORGANIZE}
            nodeType="root"
            nestedTree={
              <Fragment>
                <SidebarTreeItem
                  label="Unorganized"
                  nodeId={OrganizeViews.UNGROUPED_ITEMS}
                  nodeType="root"
                  collapseIcon={<CollapseIcon loading={ungroupedLoading} />}
                  onLabelClick={handleLabelClick(getUngroupedItemsViewPath())}
                  nestedTree={
                    <SidebarUngroupedContentGroupsTree
                      showAlert={showAlert}
                      setLoading={setUngroupedLoading}
                    />
                  }
                />

                <SidebarTreeItem
                  label="Organized"
                  nodeId={OrganizeViews.CONTENT_GROUPS}
                  nodeType="root"
                  collapseIcon={<CollapseIcon loading={modulesLoading} />}
                  onLabelClick={handleLabelClick(getContentGroupsViewPath())}
                  nestedTree={
                    <SidebarModuleTree
                      onLabelClick={handleModuleLabelClick}
                      showAlert={showAlert}
                      setLoading={setModulesLoading}
                    />
                  }
                />
              </Fragment>
            }
          />
        )}
      </TreeView>
    </Fragment>
  );
};

export default OrganizeSidebar;
